import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight, Briefcase, CalendarClock, Check, ClipboardList, FileSignature, FileUp,
  Handshake, Plus, Send, Sparkles, UserPlus, Users,
} from "lucide-react";
import { toast } from "sonner";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, KpiCard, PageHeader, StatusBadge, TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SelectField } from "@/components/forms/Fields";
import JobForm from "@/components/forms/JobForm";
import CandidateForm from "@/components/forms/CandidateForm";
import ResumeUpload from "@/components/forms/ResumeUpload";
import { InterviewForm, OfferForm } from "@/components/forms/OpsForms";

const ICONS = {
  job: Briefcase, source: UserPlus, screen: ClipboardList, submit: Send,
  interview: CalendarClock, feedback: Check, offer: FileSignature, joining: Handshake,
};

export default function Workflow() {
  const { meta, isStaff } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [jobId, setJobId] = useState(params.get("job") || null);
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  useEffect(() => {
    if (!settings) return;
    api.get("/jobs", { params: { client_id: settings.focus_client_id || undefined, limit: 60 } })
      .then((r) => {
        setJobs(r.data.items);
        setJobId((cur) => cur || r.data.items[0]?.id || null);
      })
      .catch((e) => {
        toast.error(apiError(e));
        setJobs([]);
      });
  }, [settings, refresh]);

  const load = useCallback(async () => {
    if (!jobId) return;
    setData(null);
    try {
      const { data: res } = await api.get(`/workflow/${jobId}`);
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load, refresh]);

  const done = () => {
    setDialog(null);
    setRefresh((r) => r + 1);
  };

  const partner = settings?.partner_name || "IMZ";
  const job = data?.job;

  const actions = {
    edit_job: { label: "Open the requirement", run: () => setDialog("job") },
    source: { label: "Source candidates", run: () => setDialog("source") },
    pipeline: { label: "Open pipeline board", run: () => navigate(`/jobs/${jobId}`) },
    interview: { label: "Schedule an interview", run: () => setDialog("interview") },
    feedback: { label: "Capture feedback", run: () => navigate("/interviews") },
    offer: { label: "Raise an offer", run: () => setDialog("offer") },
    joining: { label: "Update joining status", run: () => navigate("/offers") },
  };

  return (
    <div className="space-y-6">
      <PageHeader
        meta={`DIGNIFYD × ${partner} engagement`}
        title="Hiring Workflow"
        subtitle={`The full ${partner} delivery run — raise the requirement, source candidates, screen and submit, schedule interviews, capture feedback, close the offer and confirm joining. Each step shows what is already done.`}
        actions={
          isStaff && (
            <>
              <Button variant="outline" onClick={() => setDialog("job")} data-testid="workflow-new-job"
                className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
                <Plus className="h-3.5 w-3.5" /> New {partner} job
              </Button>
              <Button onClick={() => setDialog("resume")} data-testid="workflow-upload-resume"
                className="h-9 gap-2 rounded-full bg-violet-600 px-4 text-xs hover:bg-violet-500">
                <FileUp className="h-3.5 w-3.5" /> Upload resume
              </Button>
            </>
          )
        }
      />

      {!jobs ? (
        <TableSkeleton rows={4} cols={3} />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          testid="workflow-empty-state"
          title={`No ${partner} requirements yet`}
          description={`Create the first ${partner} job to start the workflow.`}
          action={isStaff && (
            <Button onClick={() => setDialog("job")} className="rounded-full bg-blue-600 hover:bg-blue-500">
              Create a job
            </Button>
          )}
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <SelectField
              label={`${partner} requirement`}
              value={jobId}
              onChange={setJobId}
              className="max-w-lg flex-1"
              testid="workflow-job-select"
              options={jobs.map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title} · ${j.location || ""}` }))}
            />
            {job && (
              <Button variant="outline" onClick={() => navigate(`/jobs/${jobId}`)} data-testid="workflow-open-job"
                className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
                Open job 360 <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {!data ? (
            <div className="space-y-4">
              <Skeleton className="h-24 bg-slate-100" />
              <Skeleton className="h-72 bg-slate-100" />
            </div>
          ) : (
            <>
              <section className="surface rounded-md p-6" data-testid="workflow-progress">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="label-xs">{job.job_code} · {job.client_name}</p>
                    <h2 className="mt-1 text-lg font-medium text-slate-900">{job.title}</h2>
                    <p className="text-xs text-slate-500">
                      {job.location} · {job.openings} opening{job.openings > 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <StatusBadge value={job.status} />
                    <p className="font-mono-num mt-2 text-2xl text-slate-900">{data.progress}%</p>
                    <p className="label-xs">workflow complete</p>
                  </div>
                </div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full rounded-full bg-blue-600 transition-all duration-700"
                    style={{ width: `${data.progress}%` }} />
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {job.required_skills.map((s) => (
                    <span key={s} className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                      {s}
                    </span>
                  ))}
                </div>
              </section>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard label="Sourced" value={data.metrics.sourced} icon={Users} testid="workflow-kpi-sourced" />
                <KpiCard label={`Submitted to ${partner}`} value={data.metrics.submitted} icon={Send} tone="amber" />
                <KpiCard label="Interviews" value={data.metrics.interviews} icon={CalendarClock} tone="violet" />
                <KpiCard label="Joined" value={data.metrics.joined} icon={Handshake} tone="emerald" />
              </div>

              <ol className="space-y-3" data-testid="workflow-steps">
                {data.steps.map((s, i) => {
                  const Icon = ICONS[s.key] || Sparkles;
                  const act = actions[s.action];
                  return (
                    <li key={s.key} data-testid="workflow-step"
                      className={`surface flex flex-col gap-4 rounded-md p-5 sm:flex-row sm:items-center ${s.done ? "border-emerald-200" : ""}`}>
                      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-[11px] font-medium ${
                        s.done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {s.done ? <Check className="h-4 w-4" /> : String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-blue-600" />
                          <p className="text-sm font-medium text-slate-900">{s.label}</p>
                          {s.done ? <StatusBadge value="Completed" /> : <StatusBadge value="Open" />}
                          {s.count > 0 && (
                            <span className="font-mono-num rounded bg-slate-100 px-1.5 text-[10px] text-slate-500">
                              {s.count}
                            </span>
                          )}
                        </div>
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{s.help}</p>
                      </div>
                      {isStaff && act && (
                        <Button size="sm" variant={s.done ? "ghost" : "default"} onClick={act.run}
                          data-testid={`workflow-action-${s.key}`}
                          className={s.done
                            ? "h-8 shrink-0 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-100"
                            : "h-8 shrink-0 gap-1.5 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500"}>
                          {act.label} <ArrowRight className="h-3 w-3" />
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </>
      )}

      <JobForm
        open={dialog === "job"}
        onOpenChange={(v) => !v && setDialog(null)}
        job={dialog === "job" && data && params.get("job") ? undefined : undefined}
        onSaved={(j) => {
          setJobId(j.id);
          done();
        }}
      />
      <CandidateForm
        open={dialog === "source"}
        onOpenChange={(v) => !v && setDialog(null)}
        initial={{ industry: "Automotive" }}
        onSaved={async (c) => {
          try {
            await api.post(`/candidates/${c.id}/apply`, { job_id: jobId, stage: "New" });
            toast.success(`${c.full_name} sourced onto ${job?.title}`);
          } catch (e) {
            toast.error(apiError(e));
          }
          done();
        }}
      />
      <ResumeUpload
        open={dialog === "resume"}
        onOpenChange={(v) => !v && setDialog(null)}
        onSaved={async (c) => {
          try {
            if (jobId) await api.post(`/candidates/${c.id}/apply`, { job_id: jobId, stage: "New" });
            toast.success(`${c.full_name} added to the pipeline`);
          } catch {
            /* candidate saved; pipeline add is optional */
          }
          done();
        }}
      />
      <InterviewForm
        open={dialog === "interview"}
        onOpenChange={(v) => !v && setDialog(null)}
        defaults={{ job_id: jobId }}
        onSaved={done}
      />
      <OfferForm
        open={dialog === "offer"}
        onOpenChange={(v) => !v && setDialog(null)}
        defaults={{ job_id: jobId }}
        onSaved={done}
      />
    </div>
  );
}
